/**
 * TeamDashboardPage - Team dashboard with roster and invite codes
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { teamsAPI } from '../services/api';
import toast from 'react-hot-toast';

const TeamDashboardPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [team, setTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showEditPlayer, setShowEditPlayer] = useState(false);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [showEditStaff, setShowEditStaff] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [playerForm, setPlayerForm] = useState({
    name: '',
    jersey_number: '',
    position: '',
  });
  const [teamForm, setTeamForm] = useState({
    name: '',
  });
  const [staffForm, setStaffForm] = useState({
    email: '',
    role: 'assistant',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTeamData();
  }, [teamId]);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      const [teamData, rosterData] = await Promise.all([
        teamsAPI.getTeam(teamId),
        teamsAPI.getRoster(teamId)
      ]);
      setTeam(teamData);
      setRoster(rosterData);

      // Load staff if user is coach/admin
      if (teamData && ['coach', 'admin'].includes(teamData.my_role)) {
        try {
          const staffData = await teamsAPI.getStaff(teamId);
          setStaff(staffData);
        } catch (staffError) {
          console.error('Error loading staff:', staffError);
          // Don't fail the whole page if staff loading fails
        }
      }
    } catch (error) {
      console.error('Error loading team data:', error);
      toast.error('Failed to load team data');
      navigate('/teams');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();

    if (!playerForm.name.trim()) {
      toast.error('Please enter a player name');
      return;
    }

    if (!playerForm.jersey_number) {
      toast.error('Please enter a jersey number');
      return;
    }

    const jerseyNum = parseInt(playerForm.jersey_number);
    if (jerseyNum < 0 || jerseyNum > 99) {
      toast.error('Jersey number must be between 0 and 99');
      return;
    }

    // Check for duplicate jersey number
    if (roster.some(p => p.jersey_number === jerseyNum)) {
      toast.error(`Jersey number #${jerseyNum} is already taken`);
      return;
    }

    try {
      setSaving(true);
      await teamsAPI.addPlayer(teamId, {
        name: playerForm.name.trim(),
        jersey_number: jerseyNum,
        position: playerForm.position || null,
      });

      toast.success(`${playerForm.name} added to roster!`);
      setShowAddPlayer(false);
      setPlayerForm({ name: '', jersey_number: '', position: '' });
      await loadTeamData();
    } catch (error) {
      console.error('Error adding player:', error);
      toast.error(error.response?.data?.detail || 'Failed to add player');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditPlayer = (player) => {
    setEditingPlayer(player);
    setPlayerForm({
      name: player.name,
      jersey_number: player.jersey_number.toString(),
      position: player.position || '',
    });
    setShowEditPlayer(true);
  };

  const handleEditPlayer = async (e) => {
    e.preventDefault();

    if (!playerForm.name.trim()) {
      toast.error('Please enter a player name');
      return;
    }

    if (!playerForm.jersey_number) {
      toast.error('Please enter a jersey number');
      return;
    }

    const jerseyNum = parseInt(playerForm.jersey_number);
    if (jerseyNum < 0 || jerseyNum > 99) {
      toast.error('Jersey number must be between 0 and 99');
      return;
    }

    // Check for duplicate jersey number (excluding current player)
    if (roster.some(p => p.id !== editingPlayer.id && p.jersey_number === jerseyNum)) {
      toast.error(`Jersey number #${jerseyNum} is already taken`);
      return;
    }

    try {
      setSaving(true);
      await teamsAPI.updatePlayer(teamId, editingPlayer.id, {
        name: playerForm.name.trim(),
        jersey_number: jerseyNum,
        position: playerForm.position || null,
      });

      toast.success(`${playerForm.name} updated successfully!`);
      setShowEditPlayer(false);
      setEditingPlayer(null);
      setPlayerForm({ name: '', jersey_number: '', position: '' });
      await loadTeamData();
    } catch (error) {
      console.error('Error updating player:', error);
      toast.error(error.response?.data?.detail || 'Failed to update player');
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePlayer = async (playerId, playerName, jerseyNumber) => {
    if (!window.confirm(`Remove ${playerName} (#${jerseyNumber}) from the roster?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await teamsAPI.removePlayer(teamId, playerId);
      toast.success(`${playerName} removed from roster`);
      await loadTeamData();
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error('Failed to remove player');
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard!`);
  };

  const handleRegenerateInvites = async () => {
    if (!window.confirm('Regenerate invite codes?\n\nThis will invalidate all existing invite links. Users with old links will no longer be able to join the team.')) {
      return;
    }

    try {
      const newCodes = await teamsAPI.regenerateInviteCodes(teamId);
      setTeam({ ...team, ...newCodes });
      toast.success('Invite codes regenerated successfully!');
    } catch (error) {
      console.error('Error regenerating codes:', error);
      toast.error('Failed to regenerate codes');
    }
  };

  const handleStartEditTeam = () => {
    setTeamForm({ name: team.name });
    setShowEditTeam(true);
  };

  const handleEditTeam = async (e) => {
    e.preventDefault();

    if (!teamForm.name.trim()) {
      toast.error('Please enter a team name');
      return;
    }

    try {
      setSaving(true);
      const updatedTeam = await teamsAPI.updateTeam(teamId, {
        name: teamForm.name.trim(),
      });

      toast.success('Team updated successfully');
      setTeam({ ...team, name: updatedTeam.name });
      setShowEditTeam(false);
      setTeamForm({ name: '' });
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error(error.response?.data?.detail || 'Failed to update team');
    } finally {
      setSaving(false);
    }
  };

  const handleStartAddStaff = () => {
    setStaffForm({ email: '', role: 'assistant' });
    setShowAddStaff(true);
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();

    if (!staffForm.email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    if (!staffForm.role) {
      toast.error('Please select a role');
      return;
    }

    try {
      setSaving(true);
      await teamsAPI.addStaff(teamId, staffForm.email.trim(), staffForm.role);

      toast.success(`${staffForm.email} added to coaching staff!`);
      setShowAddStaff(false);
      setStaffForm({ email: '', role: 'assistant' });
      await loadTeamData();
    } catch (error) {
      console.error('Error adding staff:', error);
      toast.error(error.response?.data?.detail || 'Failed to add staff member');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditStaff = (staffMember) => {
    setEditingStaff(staffMember);
    setStaffForm({ email: staffMember.email, role: staffMember.role });
    setShowEditStaff(true);
  };

  const handleEditStaff = async (e) => {
    e.preventDefault();

    if (!staffForm.role) {
      toast.error('Please select a role');
      return;
    }

    try {
      setSaving(true);
      await teamsAPI.updateStaff(teamId, editingStaff.id, staffForm.role);

      toast.success(`${editingStaff.email} role updated!`);
      setShowEditStaff(false);
      setEditingStaff(null);
      setStaffForm({ email: '', role: 'assistant' });
      await loadTeamData();
    } catch (error) {
      console.error('Error updating staff:', error);
      toast.error(error.response?.data?.detail || 'Failed to update staff member');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveStaff = async (staffMember) => {
    if (!window.confirm(`Remove ${staffMember.email} from the coaching staff?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await teamsAPI.removeStaff(teamId, staffMember.id);
      toast.success(`${staffMember.email} removed from coaching staff`);
      await loadTeamData();
    } catch (error) {
      console.error('Error removing staff:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove staff member');
    }
  };

  const canManageTeam = () => {
    return team && ['coach', 'admin'].includes(team.my_role);
  };

  const canManageRoster = () => {
    return team && ['coach', 'admin', 'assistant'].includes(team.my_role);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading team...</div>
      </div>
    );
  }

  if (!team) {
    return null;
  }

  const inviteUrl = `${window.location.origin}/register?invite=`;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <button onClick={() => navigate('/teams')} style={styles.backButton}>
            ← Back to Teams
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <h1 style={styles.title}>{team.name}</h1>
            {canManageTeam() && (
              <button onClick={handleStartEditTeam} style={styles.editTeamButton}>
                ✏️ Edit Team
              </button>
            )}
          </div>
          <div style={styles.meta}>
            <span style={styles.badge}>{team.my_role}</span>
            <span style={styles.badge}>{team.subscription_tier}</span>
            <span style={styles.infoBadge}>
              👥 {roster.length} {roster.length === 1 ? 'player' : 'players'}
            </span>
            {team.created_at && (
              <span style={styles.infoBadge}>
                📅 Created {new Date(team.created_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Invite Codes Section */}
      {canManageTeam() && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Invite Codes</h2>
            <button onClick={handleRegenerateInvites} style={styles.regenerateButton}>
              Regenerate Codes
            </button>
          </div>

          <div style={styles.inviteGrid}>
            <div style={styles.inviteCard}>
              <div style={styles.inviteLabel}>Coach Invite</div>
              <div style={styles.inviteCode}>{team.coach_invite_code}</div>
              <div style={styles.inviteUrl}>
                {inviteUrl}{team.coach_invite_code}
              </div>
              <button
                onClick={() => copyToClipboard(inviteUrl + team.coach_invite_code, 'Coach invite link')}
                style={styles.copyButton}
              >
                📋 Copy Link
              </button>
            </div>

            <div style={styles.inviteCard}>
              <div style={styles.inviteLabel}>Player Invite</div>
              <div style={styles.inviteCode}>{team.player_invite_code}</div>
              <div style={styles.inviteUrl}>
                {inviteUrl}{team.player_invite_code}
              </div>
              <button
                onClick={() => copyToClipboard(inviteUrl + team.player_invite_code, 'Player invite link')}
                style={styles.copyButton}
              >
                📋 Copy Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Roster Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Team Roster ({roster.length} players)</h2>
          {canManageRoster() && (
            <button onClick={() => setShowAddPlayer(true)} style={styles.addButton}>
              + Add Player
            </button>
          )}
        </div>

        {roster.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>👥</div>
            <p style={styles.emptyText}>No players on roster yet</p>
            {canManageRoster() && (
              <button onClick={() => setShowAddPlayer(true)} style={styles.addButton}>
                Add First Player
              </button>
            )}
          </div>
        ) : (
          <div style={styles.table}>
            <div style={styles.tableHeader}>
              <div style={styles.tableCell}>Jersey #</div>
              <div style={styles.tableCell}>Player Name</div>
              <div style={styles.tableCell}>Position</div>
              <div style={styles.tableCell}>Status</div>
              {canManageRoster() && <div style={styles.tableCell}>Actions</div>}
            </div>
            {roster.map((player) => (
              <div key={player.id} style={styles.tableRow}>
                <div style={styles.tableCell}>
                  <span style={styles.jerseyNumber}>#{player.jersey_number}</span>
                </div>
                <div style={styles.tableCell}>{player.name}</div>
                <div style={styles.tableCell}>
                  {player.position || <span style={styles.emptyValue}>-</span>}
                </div>
                <div style={styles.tableCell}>
                  {player.is_linked ? (
                    <span style={styles.linkedBadge}>✓ Linked</span>
                  ) : (
                    <span style={styles.unlinkedBadge}>Unlinked</span>
                  )}
                </div>
                {canManageRoster() && (
                  <div style={styles.tableCell}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Link
                        to={`/players/${player.id}/insights`}
                        style={styles.insightsButton}
                      >
                        📊 Stats
                      </Link>
                      <button
                        onClick={() => handleStartEditPlayer(player)}
                        style={styles.editButton}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleRemovePlayer(player.id, player.name, player.jersey_number)}
                        style={styles.removeButton}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Coaching Staff Section */}
      {canManageTeam() && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Coaching Staff ({staff.length} members)</h2>
            <button onClick={handleStartAddStaff} style={styles.addButton}>
              + Add Staff Member
            </button>
          </div>

          {staff.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>👨‍💼</div>
              <p style={styles.emptyText}>No coaching staff members yet</p>
              <button onClick={handleStartAddStaff} style={styles.addButton}>
                Add First Staff Member
              </button>
            </div>
          ) : (
            <div style={styles.table}>
              <div style={styles.staffTableHeader}>
                <div style={styles.tableCell}>Email</div>
                <div style={styles.tableCell}>Role</div>
                <div style={styles.tableCell}>Joined</div>
                <div style={styles.tableCell}>Actions</div>
              </div>
              {staff.map((member) => (
                <div key={member.id} style={styles.staffTableRow}>
                  <div style={styles.tableCell}>
                    {member.email}
                    {member.is_primary && (
                      <span style={styles.primaryBadge}>Primary</span>
                    )}
                  </div>
                  <div style={styles.tableCell}>
                    <span style={styles.roleBadge}>{member.role}</span>
                  </div>
                  <div style={styles.tableCell}>
                    {new Date(member.joined_at).toLocaleDateString()}
                  </div>
                  <div style={styles.tableCell}>
                    {!member.is_primary && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleStartEditStaff(member)}
                          style={styles.editButton}
                        >
                          Edit Role
                        </button>
                        <button
                          onClick={() => handleRemoveStaff(member)}
                          style={styles.removeButton}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Player Modal */}
      {showAddPlayer && (
        <div style={styles.modalOverlay} onClick={() => setShowAddPlayer(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Add Player to Roster</h2>
            <form onSubmit={handleAddPlayer}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Player Name *</label>
                <input
                  type="text"
                  value={playerForm.name}
                  onChange={(e) => setPlayerForm({ ...playerForm, name: e.target.value })}
                  placeholder="John Smith"
                  style={styles.input}
                  autoFocus
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Jersey Number *</label>
                <input
                  type="number"
                  value={playerForm.jersey_number}
                  onChange={(e) => setPlayerForm({ ...playerForm, jersey_number: e.target.value })}
                  placeholder="12"
                  min="0"
                  max="99"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Position (Optional)</label>
                <select
                  value={playerForm.position}
                  onChange={(e) => setPlayerForm({ ...playerForm, position: e.target.value })}
                  style={styles.input}
                >
                  <option value="">Select position</option>
                  <option value="Setter">Setter</option>
                  <option value="Outside Hitter">Outside Hitter</option>
                  <option value="Middle Blocker">Middle Blocker</option>
                  <option value="Opposite">Opposite</option>
                  <option value="Libero">Libero</option>
                  <option value="Defensive Specialist">Defensive Specialist</option>
                </select>
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowAddPlayer(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    ...styles.submitButton,
                    ...(saving ? styles.submitButtonDisabled : {})
                  }}
                >
                  {saving ? 'Adding...' : 'Add Player'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Player Modal */}
      {showEditPlayer && editingPlayer && (
        <div style={styles.modalOverlay} onClick={() => setShowEditPlayer(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Edit Player</h2>
            <form onSubmit={handleEditPlayer}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Player Name *</label>
                <input
                  type="text"
                  value={playerForm.name}
                  onChange={(e) => setPlayerForm({ ...playerForm, name: e.target.value })}
                  placeholder="John Smith"
                  style={styles.input}
                  autoFocus
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Jersey Number *</label>
                <input
                  type="number"
                  value={playerForm.jersey_number}
                  onChange={(e) => setPlayerForm({ ...playerForm, jersey_number: e.target.value })}
                  placeholder="12"
                  min="0"
                  max="99"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Position (Optional)</label>
                <select
                  value={playerForm.position}
                  onChange={(e) => setPlayerForm({ ...playerForm, position: e.target.value })}
                  style={styles.input}
                >
                  <option value="">Select position</option>
                  <option value="Setter">Setter</option>
                  <option value="Outside Hitter">Outside Hitter</option>
                  <option value="Middle Blocker">Middle Blocker</option>
                  <option value="Opposite">Opposite</option>
                  <option value="Libero">Libero</option>
                  <option value="Defensive Specialist">Defensive Specialist</option>
                </select>
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditPlayer(false);
                    setEditingPlayer(null);
                    setPlayerForm({ name: '', jersey_number: '', position: '' });
                  }}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    ...styles.submitButton,
                    ...(saving ? styles.submitButtonDisabled : {})
                  }}
                >
                  {saving ? 'Updating...' : 'Update Player'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditTeam && (
        <div style={styles.modalOverlay} onClick={() => setShowEditTeam(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Edit Team</h2>
            <form onSubmit={handleEditTeam}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Team Name *</label>
                <input
                  type="text"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                  placeholder="Team Name"
                  style={styles.input}
                  autoFocus
                  required
                />
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditTeam(false);
                    setTeamForm({ name: '' });
                  }}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    ...styles.submitButton,
                    ...(saving ? styles.submitButtonDisabled : {})
                  }}
                >
                  {saving ? 'Updating...' : 'Update Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Staff Modal */}
      {showAddStaff && (
        <div style={styles.modalOverlay} onClick={() => setShowAddStaff(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Add Staff Member</h2>
            <form onSubmit={handleAddStaff}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email Address *</label>
                <input
                  type="email"
                  value={staffForm.email}
                  onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                  placeholder="coach@example.com"
                  style={styles.input}
                  autoFocus
                  required
                />
                <p style={styles.helperText}>
                  User must already have an account with this email address.
                </p>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Role *</label>
                <select
                  value={staffForm.role}
                  onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="assistant">Assistant Coach</option>
                  <option value="coach">Head Coach</option>
                  <option value="admin">Team Admin</option>
                </select>
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddStaff(false);
                    setStaffForm({ email: '', role: 'assistant' });
                  }}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    ...styles.submitButton,
                    ...(saving ? styles.submitButtonDisabled : {})
                  }}
                >
                  {saving ? 'Adding...' : 'Add Staff Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditStaff && editingStaff && (
        <div style={styles.modalOverlay} onClick={() => setShowEditStaff(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Edit Staff Member</h2>
            <form onSubmit={handleEditStaff}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={editingStaff.email}
                  style={{ ...styles.input, background: '#f3f4f6', color: '#999' }}
                  disabled
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Role *</label>
                <select
                  value={staffForm.role}
                  onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="assistant">Assistant Coach</option>
                  <option value="coach">Head Coach</option>
                  <option value="admin">Team Admin</option>
                </select>
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditStaff(false);
                    setEditingStaff(null);
                    setStaffForm({ email: '', role: 'assistant' });
                  }}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    ...styles.submitButton,
                    ...(saving ? styles.submitButtonDisabled : {})
                  }}
                >
                  {saving ? 'Updating...' : 'Update Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  header: {
    marginBottom: '40px',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: '#667eea',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '12px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1e3a8a',
    margin: 0,
  },
  editTeamButton: {
    padding: '8px 16px',
    background: 'white',
    color: '#667eea',
    border: '2px solid #667eea',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  meta: {
    display: 'flex',
    gap: '8px',
  },
  badge: {
    padding: '4px 12px',
    background: '#f3f4f6',
    color: '#666',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  infoBadge: {
    padding: '4px 12px',
    background: '#e0e7ff',
    color: '#3730a3',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
  },
  loading: {
    textAlign: 'center',
    padding: '60px 20px',
    fontSize: '18px',
    color: '#666',
  },
  section: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1e3a8a',
    margin: 0,
  },
  regenerateButton: {
    padding: '8px 16px',
    background: 'white',
    color: '#dc2626',
    border: '2px solid #dc2626',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  addButton: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  inviteGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
  },
  inviteCard: {
    background: '#f9fafb',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  inviteLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#666',
    marginBottom: '8px',
  },
  inviteCode: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: '8px',
    fontFamily: 'monospace',
  },
  inviteUrl: {
    fontSize: '12px',
    color: '#999',
    marginBottom: '12px',
    wordBreak: 'break-all',
  },
  copyButton: {
    width: '100%',
    padding: '8px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  empty: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyText: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '20px',
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '100px 2fr 1fr 1fr 200px',
    padding: '12px',
    background: '#f9fafb',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '14px',
    color: '#666',
    marginBottom: '8px',
  },
  staffTableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 200px',
    padding: '12px',
    background: '#f9fafb',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '14px',
    color: '#666',
    marginBottom: '8px',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '100px 2fr 1fr 1fr 200px',
    padding: '16px 12px',
    borderBottom: '1px solid #f3f4f6',
    alignItems: 'center',
  },
  staffTableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 200px',
    padding: '16px 12px',
    borderBottom: '1px solid #f3f4f6',
    alignItems: 'center',
  },
  tableCell: {
    fontSize: '14px',
  },
  jerseyNumber: {
    fontWeight: 'bold',
    color: '#667eea',
  },
  emptyValue: {
    color: '#999',
  },
  linkedBadge: {
    padding: '4px 8px',
    background: '#d1fae5',
    color: '#065f46',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  unlinkedBadge: {
    padding: '4px 8px',
    background: '#f3f4f6',
    color: '#666',
    borderRadius: '4px',
    fontSize: '12px',
  },
  roleBadge: {
    padding: '4px 12px',
    background: '#e0e7ff',
    color: '#3730a3',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  primaryBadge: {
    padding: '2px 8px',
    background: '#fef3c7',
    color: '#92400e',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    marginLeft: '8px',
  },
  insightsButton: {
    padding: '6px 12px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  editButton: {
    padding: '6px 12px',
    background: 'white',
    color: '#667eea',
    border: '1px solid #667eea',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  removeButton: {
    padding: '6px 12px',
    background: 'white',
    color: '#dc2626',
    border: '1px solid #dc2626',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    borderRadius: '12px',
    padding: '32px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: '24px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    boxSizing: 'border-box',
  },
  helperText: {
    fontSize: '12px',
    color: '#666',
    marginTop: '6px',
    fontStyle: 'italic',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default TeamDashboardPage;
